import React from "react";
import { Package, Check, Plus } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

export default function ProductCard({ product }) {
  const { add, has } = useCart();
  const inCart = has(product.id);
  const soldOut = product.stock <= 0;

  const handleAdd = () => {
    if (soldOut) return;
    add({
      id: product.id,
      source: "product",
      name: product.name,
      price: product.price,
      image_url: product.image_url || "",
    });
    toast.success(`${product.name} added to cart`);
  };

  return (
    <div
      data-testid={`product-card-${product.id}`}
      className="flex flex-col bg-slate-900 border border-slate-800 rounded-xl overflow-hidden group transition-all duration-300 hover:-translate-y-1 hover:border-yellow-400/60 hover:shadow-[0_8px_30px_rgba(250,204,21,0.08)]"
    >
      <div className="relative aspect-[4/5] bg-slate-950 overflow-hidden">
        {product.image_url ? (
          <img
            src={product.image_url}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-700">
            <Package className="w-12 h-12" />
          </div>
        )}
        <div className="absolute top-3 left-3 flex flex-col gap-2">
          <span className="bg-slate-950/80 backdrop-blur text-yellow-400 text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded">
            {product.type}
          </span>
          {soldOut && (
            <span className="bg-red-500 text-white text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded">
              Sold Out
            </span>
          )}
        </div>
      </div>
      <div className="p-5 flex flex-col gap-2 flex-1">
        <div className="text-[10px] text-slate-500 font-mono uppercase tracking-[0.2em]">
          {product.set_name || "TCG"}
        </div>
        <h3 className="text-white font-bold leading-snug line-clamp-2">
          {product.name}
        </h3>
        <p className="text-xs text-slate-500 line-clamp-2 flex-1">
          {product.description}
        </p>
        <div className="flex items-center justify-between mt-3 gap-2">
          <span className="font-mono text-xl font-bold text-yellow-400">
            ${product.price.toFixed(2)}
          </span>
          <button
            type="button"
            onClick={handleAdd}
            disabled={soldOut}
            data-testid={`product-cta-${product.id}`}
            className={`inline-flex items-center gap-1 font-bold px-3 py-2 rounded-md text-xs uppercase tracking-wide transition-colors ${
              soldOut
                ? "bg-slate-700 text-slate-400 cursor-not-allowed"
                : inCart
                  ? "bg-emerald-500 text-white hover:bg-emerald-600"
                  : "bg-red-600 text-white hover:bg-red-700"
            }`}
          >
            {soldOut ? (
              "Sold out"
            ) : inCart ? (
              <>
                <Check className="w-3 h-3" /> In cart
              </>
            ) : (
              <>
                <Plus className="w-3 h-3" /> Add to cart
              </>
            )}
          </button>
        </div>
        <div className="text-[10px] text-slate-600 font-mono">
          {soldOut ? "Currently unavailable" : `${product.stock} in stock`}
        </div>
      </div>
    </div>
  );
}
